import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { VibeAuditEventReadPayloadSchema } from "../../../../schemas/VibeAuditEvent";

/**
 * Wire shape for a single VibeAuditEvent row. `payload` carries the
 * READ side of the discriminated union from `@nexus/types` — a value a
 * newer backend writes into a leniently-read field reaches an older reader
 * as that field's unlisted value, never as a failed feed page. Clients narrow on
 * `payload.eventType` and read the type-specific fields without
 * an extra cast. `eventType` at the row level mirrors
 * `payload.eventType` (the entity factory enforces this lock-step at
 * write time); both are surfaced so a caller can pre-filter at the
 * row level without unpacking the payload.
 */
export const VibeAuditEventSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  actorUserId: z.string().nullable(),
  vibeAppId: z.string().uuid().nullable(),
  payload: VibeAuditEventReadPayloadSchema,
  // The generated client re-parses the JSON wire shape, where this
  // field is an ISO STRING — a plain `z.date()` rejects it and every
  // frontend read of this schema fails validation. The union accepts
  // the server's real Date AND the wire's ISO string, narrowed BEFORE
  // conversion so the silent-epoch hazard of bare `z.coerce.date()`
  // (0 / false / "" → 1970) stays impossible.
  createdAt: z
    .union([z.date(), z.string().datetime({ offset: true })])
    .transform((value) => new Date(value))
});

export type VibeAuditEventDto = z.infer<typeof VibeAuditEventSchema>;

/**
 * Cursor pagination for the audit feed. Audit rows are append-only +
 * monotonically increasing on `createdAt` + `id` tie-break, so a
 * cursor of "`{lastCreatedAt}|{lastId}`" is sufficient — no offset
 * pagination (offset reads degrade quadratically against the growing
 * tail of the append-only table).
 *
 * `limit` is bounded so a bad caller can't accidentally page through
 * the entire org's audit history in one request.
 */
export const ListVibeAuditEventsQuerySchema = z.object({
  /// Optional filter to a specific app's events. Mismatched
  /// (vibeAppId, organizationId) returns an empty page — cross-tenant
  /// reads never leak existence signal.
  vibeAppId: z.string().uuid().optional(),
  /// Optional filter on a single event type. Derived from the
  /// generated `VIBE_AUDIT_EVENT_TYPE_VALUES` tuple so the filter
  /// stays in lockstep with the Prisma enum — adding a new audit
  /// type reaches the filter automatically via the generated
  /// values, no drift-prone manual update needed.
  eventType: z.enum(DbEnum.VIBE_AUDIT_EVENT_TYPE_VALUES).optional(),
  /// Cursor from a prior page's `nextCursor`. First-page calls omit.
  cursor: z.string().optional(),
  /// Page size; capped at 100 so a single read can't drag arbitrary
  /// amounts of the audit table into memory.
  limit: z.coerce.number().int().min(1).max(100).default(50)
});

export type ListVibeAuditEventsQuery = z.infer<typeof ListVibeAuditEventsQuerySchema>;

/**
 * Response shape for the audit feed. `nextCursor` is null when the
 * caller reached the end of the visible window — clients should
 * stop paging when this lands as null rather than continuing to
 * send the previous cursor.
 */
export const ListVibeAuditEventsResponseSchema = z.object({
  events: z.array(VibeAuditEventSchema),
  nextCursor: z.string().nullable()
});

export type ListVibeAuditEventsResponse = z.infer<typeof ListVibeAuditEventsResponseSchema>;
